import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { fmtError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Brain, ClipboardList, CheckCircle2, XCircle, AlertTriangle, ArrowRight, ArrowLeft, Clock } from "lucide-react";

const OptionList = ({ options, value, onChange, testid }) => (
  <div className="mt-5 space-y-2.5">
    {Object.entries(options).map(([k, label]) => (
      <label key={k} data-testid={`${testid}-${k}`}
        className={`flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors ${value === k ? "border-congo-gold bg-congo-goldlight" : "border-slate-200 hover:border-slate-300"}`}>
        <input type="radio" checked={value === k} onChange={() => onChange(k)} className="mt-1 accent-congo-gold" />
        <span className="text-sm text-slate-800">{label}</span>
      </label>
    ))}
  </div>
);

export default function EvaluationPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get("mode") || "full";
  const { refreshUser } = useAuth();

  const [stage, setStage] = useState("loading");
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);

  const [profileQ, setProfileQ] = useState([]);
  const [profileAns, setProfileAns] = useState({});
  const [pIdx, setPIdx] = useState(0);

  const [attemptId, setAttemptId] = useState(null);
  const [knowQ, setKnowQ] = useState([]);
  const [knowAns, setKnowAns] = useState({});
  const [kIdx, setKIdx] = useState(0);

  const [result, setResult] = useState(null);
  const [consents, setConsents] = useState({});
  const [loss, setLoss] = useState({ annual_income: "", financial_assets: "", financial_commitments: "" });
  const [busy, setBusy] = useState(false);

  const boot = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get("/eval/config"), api.get("/eval/status")]);
      setConfig(c.data); setStatus(s.data);
      setStage(mode === "profile" ? "profile" : "intro");
      if (mode === "profile") {
        const { data } = await api.get("/eval/profile/questions");
        setProfileQ(data.questions);
      }
    } catch (e) { setStage("intro"); }
  }, [mode]);

  useEffect(() => { boot(); }, [boot]);

  const startProfile = async () => {
    const { data } = await api.get("/eval/profile/questions");
    setProfileQ(data.questions); setProfileAns({}); setPIdx(0); setStage("profile");
  };

  const submitProfile = async () => {
    setBusy(true);
    try {
      await api.post("/eval/profile/submit", { answers: profileAns });
      toast.success("Profil investisseur enregistré.");
      if (mode === "profile") { navigate("/tableau-de-bord"); return; }
      if (config?.loss_sim_enabled) { setStage("losssim"); return; }
      await startKnowledge();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitLoss = async () => {
    setBusy(true);
    try {
      await api.post("/eval/loss-sim/submit", {
        annual_income: Number(loss.annual_income || 0),
        financial_assets: Number(loss.financial_assets || 0),
        financial_commitments: Number(loss.financial_commitments || 0),
      });
      toast.success("Simulation de capacité de perte enregistrée.");
      await startKnowledge();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startKnowledge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/eval/knowledge/start");
      setAttemptId(data.attempt_id); setKnowQ(data.questions); setKnowAns({}); setKIdx(0); setStage("knowledge");
    } catch (e) {
      if (e.response?.status === 429) { setResult({ blocked: true, retry_at: null, detail: fmtError(e.response?.data?.detail) }); setStage("result"); }
      else toast.error(fmtError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitKnowledge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/eval/knowledge/submit", { attempt_id: attemptId, answers: knowAns });
      setResult(data); setStage(data.passed ? "consents" : "result");
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitConsents = async () => {
    setBusy(true);
    try {
      const accepted = (config.consents || []).filter((c) => consents[c.id]).map((c) => c.id);
      await api.post("/eval/consents", { accepted });
      await refreshUser();
      setStage("done");
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <Layout><div className="max-w-2xl mx-auto px-4 py-10" data-testid="evaluation-page">{children}</div></Layout>
  );

  if (stage === "loading") return <Shell><div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-congo-blue" /></div></Shell>;

  if (stage === "intro") return (
    <Shell>
      <div className="text-center">
        <div className="h-14 w-14 rounded-2xl bg-congo-bluelight flex items-center justify-center mx-auto"><ShieldCheck className="h-7 w-7 text-congo-blue" /></div>
        <h1 className="font-head text-3xl font-bold text-slate-900 mt-4">Avant de commencer à investir</h1>
        <p className="text-slate-600 mt-3 leading-relaxed">Investir comporte des opportunités mais également des risques, notamment un risque de perte de tout ou partie du capital investi. Avant de vous permettre d'investir, nous devons mieux connaître votre situation et vérifier votre compréhension des risques.</p>
        <p className="text-slate-500 mt-2 text-sm">Cette évaluation comporte deux étapes et prend environ 5 à 10 minutes.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-5"><div className="flex items-center gap-2 text-congo-blue"><ClipboardList className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-wider">Étape 1/2</span></div><p className="font-semibold text-slate-900 mt-2">Votre profil investisseur</p><p className="text-sm text-slate-500">10 questions</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5"><div className="flex items-center gap-2 text-congo-blue"><Brain className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-wider">Étape 2/2</span></div><p className="font-semibold text-slate-900 mt-2">Vos connaissances</p><p className="text-sm text-slate-500">{config?.draw_total || 10} questions · réussite {config?.pass_score || 8}/{config?.draw_total || 10}</p></div>
      </div>
      {status?.retry_at && !status?.knowledge_current && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="eval-retry-info"><Clock className="h-5 w-5 text-amber-600 shrink-0" /><p className="text-sm text-amber-800">Nouvelle tentative du test possible à partir du {new Date(status.retry_at).toLocaleString("fr-FR")}.</p></div>
      )}
      <Button onClick={startProfile} data-testid="eval-start-btn" className="w-full mt-8 h-12 rounded-full bg-congo-gold text-slate-900 hover:bg-congo-golddark font-semibold text-base">Commencer mon évaluation <ArrowRight className="h-5 w-5 ml-1" /></Button>
    </Shell>
  );

  if (stage === "profile") {
    const q = profileQ[pIdx];
    if (!q) return <Shell><div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-congo-blue" /></div></Shell>;
    const answered = profileAns[q.id] !== undefined;
    const last = pIdx === profileQ.length - 1;
    return (
      <Shell>
        <div className="flex items-center justify-between text-sm text-slate-500"><span className="font-semibold text-congo-blue">Profil investisseur</span><span data-testid="profile-progress">Question {pIdx + 1} / {profileQ.length}</span></div>
        <Progress value={((pIdx + 1) / profileQ.length) * 100} className="h-2 mt-2" />
        <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mt-6">{q.theme}</p>
        <h2 className="font-head text-xl font-semibold text-slate-900 mt-1" data-testid={`profile-q-${q.id}`}>{q.text}</h2>
        <OptionList options={q.options} value={profileAns[q.id]} onChange={(k) => setProfileAns({ ...profileAns, [q.id]: k })} testid={`profile-opt-${q.id}`} />
        <div className="flex gap-3 mt-8">
          {pIdx > 0 && <Button variant="outline" onClick={() => setPIdx(pIdx - 1)} data-testid="profile-prev-btn" className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" /> Précédent</Button>}
          {last
            ? <Button onClick={submitProfile} disabled={!answered || busy} data-testid="profile-submit-btn" className="flex-1 rounded-full bg-congo-blue h-11">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Valider mon profil"}</Button>
            : <Button onClick={() => setPIdx(pIdx + 1)} disabled={!answered} data-testid="profile-next-btn" className="flex-1 rounded-full bg-congo-blue h-11">Suivant <ArrowRight className="h-4 w-4 ml-1" /></Button>}
        </div>
      </Shell>
    );
  }

  if (stage === "losssim") {
    const nw = (Number(loss.financial_assets || 0) - Number(loss.financial_commitments || 0));
    const capacity = Math.max(nw, 0) * 0.10;
    const valid = loss.annual_income !== "" && loss.financial_assets !== "" && loss.financial_commitments !== "";
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm"><span className="font-semibold text-congo-blue">Capacité à supporter des pertes</span></div>
        <h2 className="font-head text-xl font-semibold text-slate-900 mt-3">Simulation de votre capacité à supporter des pertes</h2>
        <p className="text-slate-600 text-sm mt-2">Ces informations servent à estimer, à titre indicatif, une capacité de perte à hauteur de 10 % de votre patrimoine net. Elles sont réexaminées chaque année.</p>
        <div className="mt-6 space-y-4">
          <div><Label>Revenus annuels (USD)</Label><Input type="number" min={0} value={loss.annual_income} onChange={(e) => setLoss({ ...loss, annual_income: e.target.value })} data-testid="loss-income" className="h-12 mt-1" /></div>
          <div><Label>Actifs financiers (USD)</Label><Input type="number" min={0} value={loss.financial_assets} onChange={(e) => setLoss({ ...loss, financial_assets: e.target.value })} data-testid="loss-assets" className="h-12 mt-1" /></div>
          <div><Label>Engagements financiers / dettes (USD)</Label><Input type="number" min={0} value={loss.financial_commitments} onChange={(e) => setLoss({ ...loss, financial_commitments: e.target.value })} data-testid="loss-commitments" className="h-12 mt-1" /></div>
        </div>
        {valid && (
          <div className="mt-5 bg-congo-bluelight/60 border border-congo-blue/20 rounded-xl p-4" data-testid="loss-preview">
            <div className="flex justify-between text-sm"><span className="text-slate-600">Patrimoine net estimé</span><span className="font-semibold text-slate-900">{money(nw)}</span></div>
            <div className="flex justify-between text-sm mt-1"><span className="text-slate-600">Capacité de perte simulée (10 %)</span><span className="font-bold text-congo-blue">{money(capacity)}</span></div>
          </div>
        )}
        <Button onClick={submitLoss} disabled={!valid || busy} data-testid="loss-submit-btn" className="w-full mt-6 h-12 rounded-full bg-congo-blue">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continuer vers le test"}</Button>
      </Shell>
    );
  }

  if (stage === "knowledge") {
    const q = knowQ[kIdx];
    if (!q) return <Shell><div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-congo-blue" /></div></Shell>;
    const answered = knowAns[q.id] !== undefined;
    const last = kIdx === knowQ.length - 1;
    return (
      <Shell>
        <div className="flex items-center justify-between text-sm text-slate-500"><span className="font-semibold text-congo-blue">Test de connaissances</span><span data-testid="knowledge-progress">Question {kIdx + 1} / {knowQ.length}</span></div>
        <Progress value={((kIdx + 1) / knowQ.length) * 100} className="h-2 mt-2" />
        <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mt-6">{q.category_label}</p>
        <h2 className="font-head text-xl font-semibold text-slate-900 mt-1" data-testid={`knowledge-q-${q.id}`}>{q.text}</h2>
        <OptionList options={q.options} value={knowAns[q.id]} onChange={(k) => setKnowAns({ ...knowAns, [q.id]: k })} testid={`knowledge-opt-${q.id}`} />
        <div className="flex gap-3 mt-8">
          {kIdx > 0 && <Button variant="outline" onClick={() => setKIdx(kIdx - 1)} data-testid="knowledge-prev-btn" className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" /> Précédent</Button>}
          {last
            ? <Button onClick={submitKnowledge} disabled={!answered || busy} data-testid="knowledge-submit-btn" className="flex-1 rounded-full bg-congo-blue h-11">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Terminer le test"}</Button>
            : <Button onClick={() => setKIdx(kIdx + 1)} disabled={!answered} data-testid="knowledge-next-btn" className="flex-1 rounded-full bg-congo-blue h-11">Suivant <ArrowRight className="h-4 w-4 ml-1" /></Button>}
        </div>
      </Shell>
    );
  }

  if (stage === "consents") return (
    <Shell>
      <div className="text-center" data-testid="eval-result-pass">
        <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
        <h1 className="font-head text-2xl font-bold text-slate-900 mt-3">Test réussi : {result.score}/{result.total}</h1>
        <p className="text-slate-600 mt-2">Dernière étape : merci de confirmer les points suivants pour activer votre accès.</p>
      </div>
      <div className="mt-6 space-y-3">
        {(config.consents || []).map((c) => (
          <label key={c.id} data-testid={`consent-${c.id}`} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-4 cursor-pointer">
            <Checkbox checked={!!consents[c.id]} onCheckedChange={(v) => setConsents({ ...consents, [c.id]: !!v })} className="mt-0.5" />
            <span className="text-sm text-slate-700">{c.text}</span>
          </label>
        ))}
      </div>
      <Button onClick={submitConsents} disabled={busy || !(config.consents || []).every((c) => consents[c.id])} data-testid="eval-consents-btn" className="w-full mt-6 h-12 rounded-full bg-congo-gold text-slate-900 hover:bg-congo-golddark font-semibold">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Activer mon accès investisseur"}</Button>
    </Shell>
  );

  if (stage === "result") return (
    <Shell>
      <div className="text-center" data-testid="eval-result-fail">
        {result?.blocked ? <Clock className="h-14 w-14 text-amber-500 mx-auto" /> : <XCircle className="h-14 w-14 text-congo-red mx-auto" />}
        <h1 className="font-head text-2xl font-bold text-slate-900 mt-3">{result?.blocked ? "Nouvelle tentative bientôt" : "Vos connaissances doivent être renforcées"}</h1>
        {!result?.blocked && <p className="font-semibold text-slate-700 mt-1">Score : {result.score}/{result.total}</p>}
        <p className="text-slate-600 mt-2">{result?.blocked ? result.detail : "Certains risques liés à l'investissement ne sont pas encore suffisamment compris. Consultez les explications avant une nouvelle évaluation."}</p>
        {result?.retry_at && <p className="text-sm text-amber-700 mt-2">Vous pourrez repasser le test à partir du {new Date(result.retry_at).toLocaleString("fr-FR")}.</p>}
      </div>
      {result?.explanations?.length > 0 && (
        <div className="mt-6 space-y-3" data-testid="eval-explanations">
          <p className="text-xs uppercase tracking-wider font-bold text-slate-400 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-congo-gold" /> Thèmes à renforcer</p>
          {result.explanations.map((ex, i) => (
            <div key={`${ex.category}-${i}`} className="bg-white border border-slate-200 rounded-xl p-3.5">
              <p className="font-semibold text-sm text-congo-blue">{ex.category}</p>
              <p className="text-sm text-slate-600 mt-1">{ex.explanation}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-3 mt-8">
        <Button variant="outline" onClick={() => navigate("/tableau-de-bord")} className="flex-1 rounded-full">Mon tableau de bord</Button>
        {!result?.blocked && !result?.retry_at && <Button onClick={startKnowledge} disabled={busy} data-testid="eval-retry-btn" className="flex-1 rounded-full bg-congo-blue">Réessayer</Button>}
      </div>
    </Shell>
  );

  if (stage === "done") return (
    <Shell>
      <div className="text-center py-6" data-testid="eval-done">
        <div className="h-16 w-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto"><ShieldCheck className="h-8 w-8 text-green-600" /></div>
        <h1 className="font-head text-2xl font-bold text-slate-900 mt-4">Votre accès investisseur est activé 🎉</h1>
        <p className="text-slate-600 mt-2">Vous pouvez désormais investir dans les projets nationaux.</p>
        <div className="flex gap-3 mt-8 justify-center">
          <Button onClick={() => navigate("/projets")} data-testid="eval-explore-btn" className="rounded-full bg-congo-gold text-slate-900 hover:bg-congo-golddark font-semibold">Explorer les projets <ArrowRight className="h-4 w-4 ml-1" /></Button>
        </div>
      </div>
    </Shell>
  );

  return <Shell><div /></Shell>;
}
